import React, { useState, useEffect } from 'react';
import { teamAPI, taskAPI, analyticsAPI } from '../services/api';
import { COGNITIVE_LANES, COGNITIVE_LEVELS } from '../mock';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, X, Calendar, Clock, Target, ChevronRight, 
  Users, Briefcase, TrendingUp, AlertTriangle, Brain, Activity, Sparkles,
  HeartHandshake, Star
} from 'lucide-react';
import { 
  BurnoutRiskCard, 
  PromotionReadinessCard, 
  LaneDriftCard, 
  StrainIndexCard 
} from '../components/TeamMemberAnalytics';
import { DESIGN } from '../components/DesignSystem';
import { useNavigate } from 'react-router-dom';
import { OneOnOnePrepModal, OneOnOnePrepButton } from '../components/OneOnOnePrep';

// ================================================
// TEAM CHEMISTRY HELPERS
// ================================================
const FRICTION_PATTERNS_SIMPLE = [
  { id: 'pace_clash', name: 'Pace Clash', lanes: ['vision', 'execution'] },
  { id: 'detail_vs_big_picture', name: 'Detail vs Big Picture', lanes: ['operations', 'strategy'] },
  { id: 'process_vs_creativity', name: 'Process vs Creativity', lanes: ['creative', 'operations'] },
  { id: 'relationship_vs_results', name: 'Relationship vs Results', lanes: ['relationships', 'revenue'] },
  { id: 'l4_ownership', name: 'L4 Territory Overlap', lanes: ['any'] },
  { id: 'culture_vs_execution', name: 'Culture vs Execution', lanes: ['culture', 'execution'] }
];

const calculateTeamChemistry = (members) => {
  if (members.length < 2) return { score: 0, hotspots: [], strongPairs: [] };
  
  let totalScore = 0;
  let pairCount = 0;
  const allPairs = [];
  
  for (let i = 0; i < members.length; i++) {
    for (let j = i + 1; j < members.length; j++) {
      const m1 = members[i];
      const m2 = members[j];
      
      let score = 70;
      let frictionRisks = [];
      
      const lanes1 = Object.keys(m1.lane_proficiency || {});
      const lanes2 = Object.keys(m2.lane_proficiency || {});
      const sharedLanes = lanes1.filter(l => lanes2.includes(l));
      const complementary = lanes1.filter(l => !lanes2.includes(l)).length + lanes2.filter(l => !lanes1.includes(l)).length;
      
      if (complementary >= 4) score += 15;
      else if (complementary >= 2) score += 8;
      
      if (sharedLanes.length >= 2) score += 10;
      else if (sharedLanes.length === 0) score -= 5;
      
      FRICTION_PATTERNS_SIMPLE.forEach(pattern => {
        if (pattern.lanes[0] === 'any') {
          const bothL4 = Object.values(m1.lane_proficiency || {}).includes('L4') && 
                        Object.values(m2.lane_proficiency || {}).includes('L4');
          if (bothL4 && sharedLanes.length > 0) {
            score -= 10;
            frictionRisks.push(pattern);
          }
        } else {
          const differentLanes = (lanes1.includes(pattern.lanes[0]) && lanes2.includes(pattern.lanes[1])) ||
                                (lanes1.includes(pattern.lanes[1]) && lanes2.includes(pattern.lanes[0]));
          if (differentLanes) {
            score -= 8;
            frictionRisks.push(pattern);
          }
        }
      });
      
      score = Math.max(40, Math.min(98, score));
      totalScore += score;
      pairCount++;
      
      allPairs.push({ m1, m2, score, frictionRisks });
    }
  }
  
  const avgScore = Math.round(totalScore / pairCount);
  const hotspots = allPairs.filter(p => p.frictionRisks.length > 0).sort((a, b) => a.score - b.score).slice(0, 3);
  const strongPairs = allPairs.sort((a, b) => b.score - a.score).slice(0, 3);
  
  return { score: avgScore, hotspots, strongPairs };
};

// Team Chemistry Card
const TeamChemistryCard = ({ teamMembers }) => {
  const navigate = useNavigate();
  const chemistry = calculateTeamChemistry(teamMembers);
  
  const getScoreColor = (score) => {
    if (score >= 85) return '#10B981';
    if (score >= 70) return '#00C9A7';
    if (score >= 55) return '#F59E0B';
    return '#EF4444';
  };
  
  const CHEMISTRY_COLOR = '#C2185B'; // Rose-purple
  
  return (
    <div 
      className="rounded-2xl overflow-hidden cursor-pointer transition-all hover:shadow-xl hover:-translate-y-1"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        boxShadow: DESIGN.shadows.lg,
        border: `1px solid ${DESIGN.colors.border}`
      }}
      onClick={() => navigate('/intelligence?tab=chemistry')}
      data-testid="team-chemistry-card"
    >
      {/* Header - Deep Rose-Purple */}
      <div 
        className="p-5"
        style={{ 
          background: 'linear-gradient(135deg, #1A0A1A 0%, #2D1535 100%)'
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'rgba(194, 24, 91, 0.3)' }}
            >
              <HeartHandshake className="w-6 h-6" style={{ color: CHEMISTRY_COLOR }} />
            </div>
            <div>
              <h3 className="font-bold text-white">Team Chemistry</h3>
              <p className="text-pink-200/70 text-sm">Cognitive compatibility</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-4xl font-black" style={{ color: CHEMISTRY_COLOR }}>{chemistry.score}%</p>
            <p className="text-pink-200/70 text-xs">
              {chemistry.score >= 75 ? 'Strong' : chemistry.score >= 60 ? 'Good' : 'Needs attention'}
            </p>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        {/* Friction Hotspots */}
        {chemistry.hotspots.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-1.5 mb-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-xs font-semibold text-gray-600">Friction Points</span>
            </div>
            <div className="space-y-2">
              {chemistry.hotspots.slice(0, 2).map((pair, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs">
                  <div className="flex -space-x-1">
                    <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-[8px] font-bold border border-white">
                      {pair.m1.avatar || pair.m1.name?.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-[8px] font-bold border border-white">
                      {pair.m2.avatar || pair.m2.name?.split(' ').map(n => n[0]).join('')}
                    </div>
                  </div>
                  <span className="text-gray-600 truncate flex-1">{pair.frictionRisks[0]?.name}</span>
                  <span className="font-bold" style={{ color: getScoreColor(pair.score) }}>{pair.score}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Strong Pairs */}
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Star className="w-3.5 h-3.5 text-emerald-500" />
            <span className="text-xs font-semibold text-gray-600">Strongest Pairs</span>
          </div>
          <div className="space-y-2">
            {chemistry.strongPairs.slice(0, 2).map((pair, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs">
                <div className="flex -space-x-1">
                  <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-[8px] font-bold border border-white">
                    {pair.m1.avatar || pair.m1.name?.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-[8px] font-bold border border-white">
                    {pair.m2.avatar || pair.m2.name?.split(' ').map(n => n[0]).join('')}
                  </div>
                </div>
                <span className="text-gray-600 truncate flex-1">{pair.m1.name?.split(' ')[0]} & {pair.m2.name?.split(' ')[0]}</span>
                <span className="font-bold" style={{ color: getScoreColor(pair.score) }}>{pair.score}%</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* View Full Analysis Link */}
        <div className="mt-4 pt-3 border-t border-gray-100">
          <div 
            className="flex items-center justify-center gap-2 text-sm font-medium"
            style={{ color: CHEMISTRY_COLOR }}
          >
            View Full Chemistry Analysis
            <ChevronRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Extended team members data
const EXTENDED_TEAM_MEMBERS = [
  { name: "Sarah Chen", title: "Head of Product", bio: "Strategic thinker with 10+ years in product leadership", lane_proficiency: { vision: "L4", strategy: "L4", creative: "L3" }, max_task_load: 8, current_load: 4, strength_notes: "Excels at strategic planning", avatar: "SC" },
  { name: "Marcus Rivera", title: "Engineering Lead", bio: "Full-stack architect passionate about scalable systems", lane_proficiency: { execution: "L4", operations: "L4", strategy: "L3" }, max_task_load: 10, current_load: 8, strength_notes: "Deep technical expertise", avatar: "MR" },
  { name: "Elena Kowalski", title: "UX Director", bio: "Human-centered design advocate with Fortune 500 experience", lane_proficiency: { creative: "L4", vision: "L3" }, max_task_load: 7, current_load: 5, strength_notes: "Design thinking expert", avatar: "EK" },
  { name: "James O'Brien", title: "VP of Sales", bio: "Revenue growth specialist with enterprise expertise", lane_proficiency: { revenue: "L4", relationships: "L4", marketing: "L3", operations: "L3" }, max_task_load: 12, current_load: 11, strength_notes: "Closes complex enterprise deals", avatar: "JO" },
  { name: "Priya Sharma", title: "Data Science Lead", bio: "ML researcher turned business strategist", lane_proficiency: { execution: "L4", strategy: "L3", operations: "L3" }, max_task_load: 9, current_load: 6, strength_notes: "Turns data into actionable insights", avatar: "PS" },
  { name: "David Kim", title: "Chief Marketing Officer", bio: "Brand builder with viral campaign expertise", lane_proficiency: { marketing: "L4", creative: "L4", relationships: "L3", vision: "L3", revenue: "L2" }, max_task_load: 10, current_load: 9, strength_notes: "Creates market-defining campaigns", avatar: "DK" },
  { name: "Angela Foster", title: "Operations Director", bio: "Six Sigma black belt, process optimization expert", lane_proficiency: { operations: "L4", execution: "L4" }, max_task_load: 8, current_load: 3, strength_notes: "Streamlines complex operations", avatar: "AF" },
  { name: "Michael Zhang", title: "Principal Architect", bio: "Cloud infrastructure and distributed systems expert", lane_proficiency: { execution: "L4", strategy: "L4", operations: "L3", vision: "L2" }, max_task_load: 11, current_load: 10, strength_notes: "Designs resilient systems at scale", avatar: "MZ" },
  { name: "Lisa Thompson", title: "Customer Success VP", bio: "Customer advocacy champion with 95% retention track record", lane_proficiency: { relationships: "L4", operations: "L3", revenue: "L3" }, max_task_load: 8, current_load: 7, strength_notes: "Builds lasting customer relationships", avatar: "LT" },
  { name: "Robert Garcia", title: "Finance Director", bio: "Strategic finance leader with IPO experience", lane_proficiency: { finance: "L4", strategy: "L4", revenue: "L3", vision: "L2" }, max_task_load: 7, current_load: 4, strength_notes: "Financial modeling and forecasting guru", avatar: "RG" },
  { name: "Jennifer Lee", title: "People Operations Lead", bio: "Culture builder focused on high-performance teams", lane_proficiency: { relationships: "L4", culture: "L4", operations: "L3" }, max_task_load: 9, current_load: 6, strength_notes: "Develops talent and culture", avatar: "JL" },
  { name: "Thomas Anderson", title: "Product Manager", bio: "Customer-obsessed PM with technical background", lane_proficiency: { vision: "L3", execution: "L3", strategy: "L2" }, max_task_load: 8, current_load: 8, strength_notes: "Ships products users love", avatar: "TA" },
  { name: "Sophia Martinez", title: "Creative Director", bio: "Award-winning designer and brand storyteller", lane_proficiency: { creative: "L4", marketing: "L4", vision: "L3", relationships: "L2", culture: "L2" }, max_task_load: 7, current_load: 5, strength_notes: "Transforms ideas into visual narratives", avatar: "SM" },
  { name: "William Johnson", title: "Security Architect", bio: "Cybersecurity expert with defense industry background", lane_proficiency: { execution: "L4", operations: "L4" }, max_task_load: 6, current_load: 2, strength_notes: "Ensures enterprise-grade security", avatar: "WJ" },
  { name: "Rachel Wong", title: "Growth Lead", bio: "Data-driven growth hacker with startup experience", lane_proficiency: { marketing: "L4", revenue: "L4", creative: "L3", execution: "L3", strategy: "L2" }, max_task_load: 10, current_load: 9, strength_notes: "Drives exponential user acquisition", avatar: "RW" }
];

// Generate unique gradient for each member
const getAvatarGradient = (name) => {
  const gradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)',
    'linear-gradient(135deg, #13547a 0%, #80d0c7 100%)',
    'linear-gradient(135deg, #ff0844 0%, #ffb199 100%)',
    'linear-gradient(135deg, #00c9ff 0%, #92fe9d 100%)',
  ];
  const hash = name.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
  return gradients[hash % gradients.length];
};

// Get capacity status color
const getCapacityColor = (percentage) => {
  if (percentage >= 90) return DESIGN.colors.error;
  if (percentage >= 70) return DESIGN.colors.amber;
  return '#10B981';
};

// Get level color
const getLevelColor = (level) => {
  return DESIGN.levels[level]?.color || DESIGN.colors.textMuted;
};

// ================================================
// LEVEL DEFINITIONS WITH FULL NAMES AND DESCRIPTIONS
// ================================================
const LEVEL_DETAILS = {
  'L1': {
    name: 'DO',
    fullName: 'L1 — DO',
    description: 'Executes defined tasks within established systems and processes.',
    shortDesc: 'Operates within the system',
    color: '#6366F1'
  },
  'L2': {
    name: 'RUN',
    fullName: 'L2 — RUN',
    description: 'Manages workflow and keeps processes organized and moving forward.',
    shortDesc: 'Manages and coordinates the system',
    color: '#8B5CF6'
  },
  'L3': {
    name: 'IMPROVE',
    fullName: 'L3 — IMPROVE',
    description: 'Recognizes patterns and strengthens the system instead of just operating inside it.',
    shortDesc: 'Strengthens and optimizes the system',
    color: '#A855F7'
  },
  'L4': {
    name: 'DECIDE + LEAD',
    fullName: 'L4 — DECIDE + LEAD',
    description: 'Makes strategic decisions that shape direction and leads others through change.',
    shortDesc: 'Shapes direction and leads change',
    color: '#C026D3'
  }
};

// Lane-specific level descriptions
const getLaneDescription = (laneId, level) => {
  const descriptions = {
    'vision': {
      'L1': 'Supports vision activities and maintains future-focused materials',
      'L2': 'Coordinates vision workshops and keeps expansion planning organized',
      'L3': 'Identifies growth patterns and strengthens the expansion strategy process',
      'L4': 'Shapes organizational direction and leads transformational initiatives'
    },
    'strategy': {
      'L1': 'Executes strategic tasks and maintains planning documentation',
      'L2': 'Manages strategic workflow and keeps architecture decisions organized',
      'L3': 'Recognizes strategic patterns and strengthens the planning system',
      'L4': 'Defines organizational strategy and leads architectural decisions'
    },
    'creative': {
      'L1': 'Produces creative assets within established brand guidelines',
      'L2': 'Coordinates creative production and manages asset workflows',
      'L3': 'Recognizes patterns in creative work and strengthens the creative system',
      'L4': 'Shapes creative direction and leads brand evolution'
    },
    'marketing': {
      'L1': 'Executes marketing campaigns within defined playbooks',
      'L2': 'Manages marketing channels and coordinates campaign delivery',
      'L3': 'Identifies market patterns and optimizes marketing performance',
      'L4': 'Defines marketing strategy and leads market positioning'
    },
    'operations': {
      'L1': 'Executes operational tasks according to established procedures',
      'L2': 'Manages operational workflows and keeps processes running smoothly',
      'L3': 'Identifies efficiency patterns and improves operational systems',
      'L4': 'Designs operational architecture and leads process transformation'
    },
    'execution': {
      'L1': 'Delivers assigned work within project frameworks',
      'L2': 'Coordinates execution across team members and tracks delivery',
      'L3': 'Optimizes delivery processes and strengthens execution quality',
      'L4': 'Defines execution standards and leads delivery excellence'
    },
    'revenue': {
      'L1': 'Supports revenue activities and maintains sales materials',
      'L2': 'Manages revenue pipelines and coordinates deal progression',
      'L3': 'Identifies revenue patterns and optimizes sales performance',
      'L4': 'Shapes revenue strategy and leads commercial growth'
    },
    'relationships': {
      'L1': 'Supports relationship activities and maintains stakeholder records',
      'L2': 'Coordinates stakeholder communications and manages touchpoints',
      'L3': 'Strengthens relationship systems and identifies partnership opportunities',
      'L4': 'Shapes partnership strategy and leads key relationships'
    },
    'culture': {
      'L1': 'Participates in culture initiatives and supports team activities',
      'L2': 'Coordinates culture programs and manages team engagement',
      'L3': 'Identifies culture patterns and strengthens team dynamics',
      'L4': 'Shapes organizational culture and leads values evolution'
    },
    'finance': {
      'L1': 'Executes financial tasks within established procedures',
      'L2': 'Manages financial workflows and coordinates reporting',
      'L3': 'Identifies financial patterns and optimizes resource allocation',
      'L4': 'Shapes financial strategy and leads fiscal decisions'
    }
  };
  return descriptions[laneId]?.[level] || LEVEL_DETAILS[level]?.description || 'Operates within this cognitive domain';
};

// ================================================
// COGNITIVE IDENTITY CARD COMPONENT
// ================================================
const CognitiveIdentityCard = ({ member, proficientLanes }) => {
  // Find primary lane (highest level lane)
  const sortedLanes = [...proficientLanes].sort((a, b) => {
    const levelOrder = { 'L4': 4, 'L3': 3, 'L2': 2, 'L1': 1 };
    return (levelOrder[b[1]] || 0) - (levelOrder[a[1]] || 0);
  });
  
  const primaryLane = sortedLanes[0];
  const supportingLanes = sortedLanes.slice(1);
  
  // Overall level is the highest level across all lanes
  const overallLevel = primaryLane?.[1] || 'L2';
  const levelDetails = LEVEL_DETAILS[overallLevel];
  
  // Level journey
  const levelOrder = ['L1', 'L2', 'L3', 'L4'];
  const currentIndex = levelOrder.indexOf(overallLevel);
  
  return (
    <div className="space-y-6">
      {/* Overall Level Display */}
      <div 
        className="p-6 rounded-2xl relative overflow-hidden"
        style={{ 
          background: `linear-gradient(135deg, ${DESIGN.colors.primary}08 0%, ${DESIGN.colors.primary}15 100%)`,
          border: `1px solid ${DESIGN.colors.primary}25`
        }}
      >
        {/* Decorative background */}
        <div 
          className="absolute top-0 right-0 w-32 h-32 opacity-10"
          style={{
            background: `radial-gradient(circle at top right, ${levelDetails?.color || DESIGN.colors.primary} 0%, transparent 70%)`
          }}
        />
        
        <div className="relative">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4" style={{ color: DESIGN.colors.primary }} />
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.primary }}>
              Organizational Level
            </span>
          </div>
          
          <div className="flex items-start gap-6">
            {/* Level Badge */}
            <div 
              className="flex flex-col items-center justify-center w-24 h-24 rounded-2xl flex-shrink-0"
              style={{ 
                background: `linear-gradient(135deg, ${levelDetails?.color || DESIGN.colors.primary} 0%, ${DESIGN.colors.primary} 100%)`,
                boxShadow: `0 8px 32px ${levelDetails?.color || DESIGN.colors.primary}40`
              }}
            >
              <span className="text-3xl font-black text-white">{overallLevel}</span>
              <span className="text-xs font-bold text-white/80 uppercase tracking-wider">
                {levelDetails?.name}
              </span>
            </div>
            
            {/* Level Description */}
            <div className="flex-1 pt-1">
              <h3 className="text-xl font-bold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
                {levelDetails?.fullName}
              </h3>
              <p className="text-sm leading-relaxed mb-4" style={{ color: DESIGN.colors.textSecondary }}>
                {levelDetails?.description}
              </p>
              
              {/* Level Journey */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold" style={{ color: DESIGN.colors.textMuted }}>
                  Growth Journey:
                </span>
                <div className="flex items-center gap-1">
                  {levelOrder.map((level, idx) => {
                    const isCurrent = level === overallLevel;
                    const isPast = idx < currentIndex;
                    const isFuture = idx > currentIndex;
                    
                    return (
                      <React.Fragment key={level}>
                        <div 
                          className={`px-2 py-0.5 rounded-md text-xs font-bold transition-all ${
                            isCurrent ? 'scale-110' : ''
                          }`}
                          style={{ 
                            backgroundColor: isCurrent 
                              ? DESIGN.colors.primary 
                              : isPast 
                                ? `${DESIGN.colors.primary}30`
                                : DESIGN.colors.background,
                            color: isCurrent 
                              ? 'white' 
                              : isPast 
                                ? DESIGN.colors.primary
                                : DESIGN.colors.textMuted,
                            border: isFuture ? `1px dashed ${DESIGN.colors.border}` : 'none'
                          }}
                        >
                          {level}
                        </div>
                        {idx < levelOrder.length - 1 && (
                          <ChevronRight 
                            className="w-3 h-3" 
                            style={{ 
                              color: isPast || isCurrent ? DESIGN.colors.primary : DESIGN.colors.textMuted,
                              opacity: isFuture ? 0.4 : 1
                            }} 
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Lane Proficiency Section */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Target className="w-4 h-4" style={{ color: DESIGN.colors.textMuted }} />
          <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>
            Cognitive Lane Profile
          </h3>
        </div>
        
        <div className="space-y-3">
          {/* Primary Lane - Featured Card */}
          {primaryLane && (() => {
            const [laneId, level] = primaryLane;
            const lane = COGNITIVE_LANES.find(l => l.id === laneId);
            const levelInfo = LEVEL_DETAILS[level];
            
            return (
              <div 
                className="p-5 rounded-2xl relative overflow-hidden"
                style={{ 
                  backgroundColor: `${lane?.color}08`,
                  borderLeft: `5px solid ${lane?.color}`,
                  boxShadow: `0 4px 20px ${lane?.color}15`
                }}
              >
                {/* Primary badge */}
                <div className="absolute top-4 right-4">
                  <span 
                    className="px-3 py-1 rounded-full text-xs font-bold"
                    style={{ 
                      backgroundColor: `${DESIGN.colors.primary}20`,
                      color: DESIGN.colors.primary
                    }}
                  >
                    Primary Lane
                  </span>
                </div>
                
                {/* Lane color accent in corner */}
                <div 
                  className="absolute bottom-0 right-0 w-24 h-24 opacity-10"
                  style={{
                    background: `radial-gradient(circle at bottom right, ${lane?.color} 0%, transparent 70%)`
                  }}
                />
                
                <div className="relative">
                  <div className="flex items-start gap-4 mb-4">
                    {/* Lane indicator */}
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ 
                        backgroundColor: `${lane?.color}25`,
                        border: `2px solid ${lane?.color}50`
                      }}
                    >
                      <div 
                        className="w-6 h-6 rounded-full"
                        style={{ backgroundColor: lane?.color }}
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h4 className="text-lg font-bold mb-1" style={{ color: DESIGN.colors.textPrimary }}>
                        {lane?.name}
                      </h4>
                      
                      {/* Level Badge */}
                      <div 
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg"
                        style={{ 
                          backgroundColor: levelInfo?.color || lane?.color,
                          boxShadow: `0 2px 8px ${levelInfo?.color || lane?.color}40`
                        }}
                      >
                        <span className="text-sm font-black text-white">{level}</span>
                        <span className="text-xs font-bold text-white/90 uppercase tracking-wide">
                          {levelInfo?.name}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Description */}
                  <p className="text-sm leading-relaxed" style={{ color: DESIGN.colors.textSecondary }}>
                    {getLaneDescription(laneId, level)}
                  </p>
                </div>
              </div>
            );
          })()}
          
          {/* Supporting Lanes */}
          {supportingLanes.length > 0 && (
            <div className="grid grid-cols-1 gap-3">
              {supportingLanes.map(([laneId, level]) => {
                const lane = COGNITIVE_LANES.find(l => l.id === laneId);
                const levelInfo = LEVEL_DETAILS[level];
                
                return (
                  <div 
                    key={laneId}
                    className="p-4 rounded-xl flex items-start gap-4 transition-all hover:shadow-md"
                    style={{ 
                      backgroundColor: DESIGN.colors.card,
                      borderLeft: `4px solid ${lane?.color}`,
                      border: `1px solid ${DESIGN.colors.border}`
                    }}
                  >
                    {/* Lane indicator */}
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${lane?.color}15` }}
                    >
                      <div 
                        className="w-5 h-5 rounded-full"
                        style={{ backgroundColor: lane?.color }}
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-sm font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                          {lane?.name}
                        </h4>
                        
                        {/* Level Badge - Compact */}
                        <div 
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md"
                          style={{ backgroundColor: `${levelInfo?.color || lane?.color}20` }}
                        >
                          <span className="text-xs font-black" style={{ color: levelInfo?.color || lane?.color }}>
                            {level}
                          </span>
                          <span className="text-xs font-semibold" style={{ color: levelInfo?.color || lane?.color }}>
                            {levelInfo?.name}
                          </span>
                        </div>
                      </div>
                      
                      {/* Description */}
                      <p className="text-xs leading-relaxed" style={{ color: DESIGN.colors.textMuted }}>
                        {getLaneDescription(laneId, level)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Task Detail Side Panel
const TaskDetailPanel = ({ member, tasks, onClose }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  const memberId = member?.id || member?._id;

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!memberId) return;
      setLoadingAnalytics(true);
      try {
        const response = await analyticsAPI.getMemberAnalytics(memberId);
        setAnalytics(response.data);
      } catch (error) {
        console.error('Error fetching member analytics:', error);
      } finally {
        setLoadingAnalytics(false);
      }
    };
    fetchAnalytics();
  }, [memberId]);

  if (!member) return null;

  const proficientLanes = Object.entries(member.lane_proficiency || {});
  const loadPercentage = Math.round((member.current_load / member.max_task_load) * 100);
  const capacityColor = getCapacityColor(loadPercentage);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="task-detail-panel">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      
      <div 
        className="relative w-full max-w-xl shadow-2xl overflow-y-auto animate-slide-in"
        style={{ backgroundColor: DESIGN.colors.card }}
      >
        {/* Header */}
        <div 
          className="sticky top-0 z-10 p-6"
          style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div 
                  className="w-20 h-20 rounded-2xl flex items-center justify-center text-2xl font-bold text-white"
                  style={{ background: getAvatarGradient(member.name) }}
                >
                  {member.avatar}
                </div>
                <div 
                  className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-3 border-white"
                  style={{ 
                    backgroundColor: capacityColor,
                    boxShadow: `0 0 10px ${capacityColor}`
                  }}
                />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{member.name}</h2>
                <p className="text-sm text-slate-300">{member.title}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl transition-colors">
              <X className="w-5 h-5 text-white/80" />
            </button>
          </div>
          
          {/* Load Summary */}
          <div className="mt-5 p-4 rounded-2xl bg-white/10 backdrop-blur">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-slate-300">Workload Capacity</span>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-black text-white">{member.current_load}</span>
                <span className="text-lg text-slate-400">/{member.max_task_load}</span>
              </div>
            </div>
            <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
              <div 
                className="h-full rounded-full transition-all duration-500"
                style={{ 
                  width: `${loadPercentage}%`,
                  background: `linear-gradient(90deg, ${capacityColor} 0%, ${capacityColor}CC 100%)`,
                  boxShadow: loadPercentage > 85 ? `0 0 15px ${capacityColor}` : 'none',
                  animation: loadPercentage > 85 ? 'pulse 2s infinite' : 'none'
                }}
              />
            </div>
            <p 
              className="text-right text-sm font-bold mt-2"
              style={{ color: capacityColor }}
            >
              {loadPercentage}%
            </p>
          </div>
        </div>
        
        {/* Content with Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="px-6 pt-4 mb-0 border-b grid grid-cols-3" style={{ backgroundColor: DESIGN.colors.background, borderColor: DESIGN.colors.border }}>
            <TabsTrigger value="overview" className="text-sm gap-1.5">
              <Users className="w-3.5 h-3.5" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="analytics" className="text-sm gap-1.5">
              <Brain className="w-3.5 h-3.5" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="tasks" className="text-sm gap-1.5">
              <Briefcase className="w-3.5 h-3.5" />
              Tasks ({tasks.length})
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="p-6 space-y-5 mt-0">
            {/* Bio */}
            <div 
              className="p-4 rounded-xl"
              style={{ backgroundColor: DESIGN.colors.background }}
            >
              <p className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>{member.bio}</p>
            </div>

            {/* Cognitive Identity Card - Redesigned Lane Proficiency */}
            <CognitiveIdentityCard 
              member={member}
              proficientLanes={proficientLanes}
            />

            {/* AI Insight Strip */}
            <div 
              className="p-4 rounded-xl relative overflow-hidden"
              style={{ 
                background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)',
                borderLeft: `4px solid ${DESIGN.colors.amber}`
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4" style={{ color: DESIGN.colors.amber }} />
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#92400E' }}>
                  AI Insight
                </span>
              </div>
              <p className="text-sm font-medium" style={{ color: '#78350F' }}>{member.strength_notes}</p>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="p-6 space-y-4 mt-0">
            {loadingAnalytics ? (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center gap-2">
                  <Activity className="w-5 h-5 animate-pulse" style={{ color: DESIGN.colors.primary }} />
                  <span className="text-sm" style={{ color: DESIGN.colors.textMuted }}>Loading analytics...</span>
                </div>
              </div>
            ) : analytics ? (
              <>
                <BurnoutRiskCard data={analytics.burnout_risk} />
                <PromotionReadinessCard data={analytics.promotion_readiness} />
                <LaneDriftCard data={analytics.lane_drift} />
                <StrainIndexCard data={analytics.strain_index} />
              </>
            ) : (
              <div className="text-center py-8">
                <Brain className="w-8 h-8 mx-auto mb-2" style={{ color: DESIGN.colors.textMuted }} />
                <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>Analytics not available</p>
              </div>
            )}
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks" className="p-6 space-y-4 mt-0">
            {tasks.length === 0 ? (
              <div className="text-center py-8 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
                <Briefcase className="w-8 h-8 mx-auto mb-2" style={{ color: DESIGN.colors.textMuted }} />
                <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>No tasks assigned</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tasks.map((task) => {
                  const lane = COGNITIVE_LANES.find(l => l.id === task.primary_lane);
                  const statusColors = {
                    'Active': { bg: DESIGN.colors.primarySubtle, text: DESIGN.colors.primary },
                    'In Review': { bg: DESIGN.colors.blueSubtle, text: DESIGN.colors.blue },
                    'Completed': { bg: DESIGN.colors.successSubtle, text: DESIGN.colors.success },
                    'Planned': { bg: DESIGN.colors.background, text: DESIGN.colors.textSecondary },
                  };
                  const statusColor = statusColors[task.status] || statusColors.Planned;

                  return (
                    <div 
                      key={task.id || task._id} 
                      className="p-4 rounded-xl border-l-4 hover:shadow-md transition-all"
                      style={{ 
                        backgroundColor: DESIGN.colors.card,
                        borderLeftColor: lane?.color || DESIGN.colors.border,
                        boxShadow: DESIGN.shadows.sm
                      }}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h4 className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>{task.title}</h4>
                        <span 
                          className="text-xs px-3 py-1 rounded-full font-semibold flex-shrink-0"
                          style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
                        >
                          {task.status}
                        </span>
                      </div>
                      <p className="text-xs mb-3 line-clamp-2" style={{ color: DESIGN.colors.textMuted }}>{task.description}</p>
                      <div className="flex items-center gap-3 text-xs" style={{ color: DESIGN.colors.textMuted }}>
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: lane?.color }} />
                          <span>{lane?.name || task.primary_lane}</span>
                        </div>
                        {task.due_date && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            <span>{new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <style>{`
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
};

// Premium Member Card
const MemberCard = ({ member, onClick, onPrepClick }) => {
  const proficientLanes = Object.entries(member.lane_proficiency || {});
  const loadPercentage = Math.round((member.current_load / member.max_task_load) * 100);
  const capacityColor = getCapacityColor(loadPercentage);
  const isAtCapacity = loadPercentage >= 90;

  return (
    <div 
      onClick={onClick}
      className="group rounded-2xl p-6 cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        boxShadow: DESIGN.shadows.md,
        border: `1px solid ${DESIGN.colors.border}`
      }}
      data-testid={`member-card-${member.id || member.name}`}
    >
      {/* Avatar with Capacity Ring */}
      <div className="flex items-start gap-4 mb-5">
        <div className="relative">
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center text-xl font-bold text-white"
            style={{ 
              background: getAvatarGradient(member.name),
              boxShadow: `0 0 0 4px ${DESIGN.colors.card}, 0 0 0 7px ${capacityColor}`,
            }}
          >
            {member.avatar}
          </div>
          {/* Capacity indicator dot */}
          <div 
            className="absolute bottom-0 right-0 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center"
            style={{ 
              backgroundColor: capacityColor,
              boxShadow: isAtCapacity ? `0 0 8px ${capacityColor}` : 'none'
            }}
          >
            {isAtCapacity && <span className="text-white text-[8px] font-bold">!</span>}
          </div>
        </div>
        <div className="flex-1 min-w-0 pt-1">
          <h3 className="text-lg font-bold truncate" style={{ color: DESIGN.colors.textPrimary }}>{member.name}</h3>
          <p className="text-sm font-medium truncate" style={{ color: DESIGN.colors.textSecondary }}>{member.title}</p>
          <p className="text-xs mt-1 line-clamp-2" style={{ color: DESIGN.colors.textMuted }}>{member.bio}</p>
        </div>
      </div>

      {/* Load Numbers */}
      <div 
        className="flex items-center justify-between p-4 rounded-xl mb-4"
        style={{ backgroundColor: DESIGN.colors.background }}
      >
        <div className="text-center">
          <p className="text-2xl font-black" style={{ color: DESIGN.colors.textPrimary }}>{member.max_task_load}</p>
          <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: DESIGN.colors.textMuted }}>Max Load</p>
        </div>
        <div className="text-3xl font-light" style={{ color: DESIGN.colors.border }}>/</div>
        <div className="text-center">
          <p 
            className="text-2xl font-black"
            style={{ color: capacityColor }}
          >
            {member.current_load}
          </p>
          <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: DESIGN.colors.textMuted }}>Current</p>
        </div>
      </div>

      {/* Capacity Bar */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold" style={{ color: DESIGN.colors.textMuted }}>Capacity</span>
          <span 
            className="text-sm font-black"
            style={{ color: capacityColor }}
          >
            {loadPercentage}%
          </span>
        </div>
        <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: '#E5E7EB' }}>
          <div 
            className="h-full rounded-full transition-all duration-700"
            style={{ 
              width: `${loadPercentage}%`,
              background: loadPercentage >= 90 
                ? `linear-gradient(90deg, ${DESIGN.colors.error} 0%, #F87171 100%)`
                : loadPercentage >= 70 
                  ? `linear-gradient(90deg, ${DESIGN.colors.amber} 0%, #FCD34D 100%)`
                  : `linear-gradient(90deg, #10B981 0%, #34D399 100%)`,
              boxShadow: isAtCapacity ? `0 0 10px ${capacityColor}50` : 'none',
              animation: isAtCapacity ? 'barPulse 2s infinite' : 'none'
            }}
          />
        </div>
      </div>

      {/* Lane Proficiency Badges */}
      <div className="mb-4">
        <h4 className="text-xs font-semibold mb-2" style={{ color: DESIGN.colors.textMuted }}>Lane Proficiency</h4>
        <div className="flex flex-wrap gap-1.5">
          {proficientLanes.slice(0, 5).map(([laneId, level]) => {
            const lane = COGNITIVE_LANES.find(l => l.id === laneId);
            const levelColor = getLevelColor(level);
            return (
              <div 
                key={laneId}
                className="group/badge relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
                style={{ backgroundColor: `${lane?.color}15` }}
              >
                <div 
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: lane?.color }}
                />
                <span 
                  className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: levelColor, color: 'white' }}
                >
                  {level}
                </span>
                {/* Lane name tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 rounded bg-slate-800 text-white text-[10px] whitespace-nowrap opacity-0 group-hover/badge:opacity-100 transition-opacity z-10">
                  {lane?.name}
                </div>
              </div>
            );
          })}
          {proficientLanes.length > 5 && (
            <div 
              className="flex items-center px-2.5 py-1.5 rounded-lg text-[10px] font-semibold"
              style={{ backgroundColor: DESIGN.colors.background, color: DESIGN.colors.textMuted }}
            >
              +{proficientLanes.length - 5}
            </div>
          )}
        </div>
      </div>

      {/* AI Insight Strip */}
      <div 
        className="p-3 rounded-xl mb-4 relative overflow-hidden"
        style={{ 
          background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.12) 0%, rgba(245, 158, 11, 0.06) 100%)',
          borderLeft: `3px solid ${DESIGN.colors.amber}`
        }}
      >
        <div className="flex items-center gap-1.5 mb-1">
          <Sparkles className="w-3 h-3" style={{ color: DESIGN.colors.amber }} />
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#92400E' }}>AI Insight</span>
        </div>
        <p className="text-xs" style={{ color: '#78350F' }}>{member.strength_notes}</p>
      </div>

      {/* View Tasks Link */}
      <button 
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all group-hover:gap-3"
        style={{ 
          color: DESIGN.colors.primary,
          backgroundColor: DESIGN.colors.primarySubtle
        }}
        onClick={(e) => e.stopPropagation()}
      >
        View Tasks
        <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
      </button>
      
      {/* 1:1 Prep Button */}
      <button 
        onClick={(e) => {
          e.stopPropagation();
          onPrepClick && onPrepClick(member);
        }}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all mt-2"
        style={{ 
          background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
          color: 'white',
          boxShadow: DESIGN.shadows.glowPrimary
        }}
        data-testid={`one-on-one-prep-btn-${member.id || member.name}`}
      >
        <Users className="w-4 h-4" />
        1:1 Prep
      </button>

      <style>{`
        @keyframes barPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

// Hero Stat Card
const HeroStatCard = ({ value, label, icon: Icon, color, isAlert = false, hasGlow = false }) => (
  <div 
    className="rounded-2xl p-6 transition-all hover:shadow-lg"
    style={{ 
      backgroundColor: DESIGN.colors.card,
      boxShadow: hasGlow ? `0 0 30px ${color}30, ${DESIGN.shadows.lg}` : DESIGN.shadows.lg,
      border: `1px solid ${DESIGN.colors.border}`
    }}
  >
    <div className="flex items-center gap-4">
      <div 
        className="w-14 h-14 rounded-2xl flex items-center justify-center"
        style={{ 
          backgroundColor: `${color}15`,
          boxShadow: hasGlow ? `0 0 20px ${color}40` : 'none'
        }}
      >
        <Icon className="w-7 h-7" style={{ color }} />
      </div>
      <div>
        <p 
          className="text-4xl font-black"
          style={{ 
            color: isAlert && value > 0 ? DESIGN.colors.error : DESIGN.colors.textPrimary,
            textShadow: isAlert && value > 0 ? `0 0 20px ${DESIGN.colors.error}40` : 'none'
          }}
        >
          {value}
        </p>
        <p className="text-sm font-medium" style={{ color: DESIGN.colors.textMuted }}>{label}</p>
      </div>
    </div>
  </div>
);

const Team = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [prepMember, setPrepMember] = useState(null); // For 1:1 Prep modal

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [teamRes, tasksRes] = await Promise.all([
          teamAPI.getAll(),
          taskAPI.getAll()
        ]);
        
        let members = teamRes.data.team_members || [];
        const tasks = tasksRes.data.tasks || [];
        
        if (members.length >= 15) {
          members = members.map(member => {
            const memberTasks = tasks.filter(t => t.owner_id === member.id || t.owner_id === member._id);
            return { ...member, current_load: memberTasks.length || member.current_load || 0 };
          });
        } else if (members.length > 0) {
          const existingNames = new Set(members.map(m => m.name));
          const additionalMembers = EXTENDED_TEAM_MEMBERS
            .filter(m => !existingNames.has(m.name))
            .slice(0, 15 - members.length)
            .map((m, idx) => ({ ...m, id: `extended-${idx}`, org_id: members[0]?.org_id || 'demo' }));
          
          members = members.map((m) => {
            const memberTasks = tasks.filter(t => t.owner_id === m.id || t.owner_id === m._id);
            const extMember = EXTENDED_TEAM_MEMBERS.find(em => em.name === m.name);
            return {
              ...m,
              lane_proficiency: m.lane_proficiency || extMember?.lane_proficiency || {},
              strength_notes: m.strength_notes || extMember?.strength_notes || '',
              current_load: memberTasks.length || m.current_load || 0
            };
          });
          
          members = [...members, ...additionalMembers];
        } else {
          members = EXTENDED_TEAM_MEMBERS.slice(0, 15).map((m, idx) => ({ ...m, id: `extended-${idx}` }));
        }
        
        setTeamMembers(members.slice(0, 15));
        setAllTasks(tasks);
      } catch (error) {
        console.error('Error fetching data:', error);
        setTeamMembers(EXTENDED_TEAM_MEMBERS.slice(0, 15).map((m, idx) => ({ ...m, id: `extended-${idx}` })));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getMemberTasks = (member) => {
    const memberId = member.id || member._id;
    return allTasks.filter(task => task.owner_id === memberId);
  };

  const totalCapacity = teamMembers.reduce((acc, m) => acc + (m.max_task_load || 0), 0);
  const totalLoad = teamMembers.reduce((acc, m) => acc + (m.current_load || 0), 0);
  const overloadedCount = teamMembers.filter(m => (m.current_load / m.max_task_load) > 0.9).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '500px' }}>
        <div className="flex flex-col items-center gap-4">
          <div 
            className="w-14 h-14 rounded-full border-4 border-t-transparent animate-spin"
            style={{ borderColor: `${DESIGN.colors.primary}30`, borderTopColor: DESIGN.colors.primary }}
          />
          <p className="text-sm font-medium" style={{ color: DESIGN.colors.textMuted }}>Loading team...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="team-page">
      {/* Premium Page Header */}
      <div className="relative">
        <div 
          className="absolute inset-0 rounded-3xl opacity-30"
          style={{
            background: 'linear-gradient(135deg, rgba(0, 201, 167, 0.1) 0%, rgba(139, 92, 246, 0.05) 50%, rgba(59, 130, 246, 0.05) 100%)'
          }}
        />
        <div className="relative flex items-center justify-between py-6">
          <div className="flex items-center gap-4">
            <div 
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{ 
                background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                boxShadow: DESIGN.shadows.glowPrimary
              }}
            >
              <Users className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight" style={{ color: DESIGN.colors.textPrimary }}>
                TEAM
              </h1>
              <p className="text-base" style={{ color: DESIGN.colors.textSecondary }}>
                Talent intelligence and cognitive profiles
              </p>
            </div>
          </div>
          
          <button 
            className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition-all hover:-translate-y-0.5"
            style={{ 
              background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
              color: 'white',
              boxShadow: DESIGN.shadows.glowPrimary
            }}
          >
            <Plus className="w-5 h-5" />
            Add Member
          </button>
        </div>
      </div>

      {/* Hero Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <HeroStatCard 
          value={teamMembers.length} 
          label="Team Members" 
          icon={Users} 
          color={DESIGN.colors.primary}
        />
        <HeroStatCard 
          value={totalCapacity} 
          label="Total Capacity" 
          icon={Briefcase} 
          color={DESIGN.colors.blue}
        />
        <HeroStatCard 
          value={totalLoad} 
          label="Current Load" 
          icon={TrendingUp} 
          color={DESIGN.colors.amber}
        />
        <HeroStatCard 
          value={overloadedCount} 
          label="At Capacity" 
          icon={AlertTriangle} 
          color={DESIGN.colors.error}
          isAlert={true}
          hasGlow={overloadedCount > 0}
        />
      </div>

      {/* Team Chemistry Card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <TeamChemistryCard teamMembers={teamMembers} />
        </div>
        <div className="lg:col-span-2 rounded-2xl p-5" style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.md, border: `1px solid ${DESIGN.colors.border}` }}>
          <div className="flex items-center gap-2 mb-3">
            <Brain className="w-5 h-5" style={{ color: DESIGN.colors.primary }} />
            <h3 className="font-bold" style={{ color: DESIGN.colors.textPrimary }}>Team Cognitive Distribution</h3>
          </div>
          <div className="grid grid-cols-5 gap-3">
            {['vision', 'strategy', 'creative', 'operations', 'execution'].map(lane => {
              const laneConfig = COGNITIVE_LANES.find(l => l.id === lane);
              const count = teamMembers.filter(m => Object.keys(m.lane_proficiency || {}).includes(lane)).length;
              const l4Count = teamMembers.filter(m => (m.lane_proficiency || {})[lane] === 'L4').length;
              return (
                <div key={lane} className="text-center p-3 rounded-xl" style={{ backgroundColor: `${laneConfig?.color}10` }}>
                  <div className="w-8 h-8 mx-auto rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: `${laneConfig?.color}20` }}>
                    <span className="text-lg">{laneConfig?.icon}</span>
                  </div>
                  <p className="text-xs font-medium capitalize" style={{ color: laneConfig?.color }}>{lane}</p>
                  <p className="text-lg font-bold" style={{ color: DESIGN.colors.textPrimary }}>{count}</p>
                  <p className="text-[10px]" style={{ color: DESIGN.colors.textMuted }}>{l4Count} at L4</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Team Members Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {teamMembers.map((member, idx) => (
          <MemberCard 
            key={member.id || member._id || idx}
            member={member}
            onClick={() => setSelectedMember(member)}
            onPrepClick={(member) => setPrepMember(member)}
          />
        ))}
      </div>

      {selectedMember && (
        <TaskDetailPanel 
          member={selectedMember}
          tasks={getMemberTasks(selectedMember)}
          onClose={() => setSelectedMember(null)}
        />
      )}
      
      {/* 1:1 Prep Modal */}
      <OneOnOnePrepModal
        isOpen={!!prepMember}
        onClose={() => setPrepMember(null)}
        member={prepMember}
      />
    </div>
  );
};

export default Team;
